import { useStore } from '../stores/store';
import { clearPersistedHubSession, getPersistedHubToken } from '../utils/session';

const API_BASE = import.meta.env.VITE_API_URL || 'https://puppypad-email-automation.gulfam.workers.dev/api';

function getAuthToken() {
  return useStore.getState().token || getPersistedHubToken();
}

async function readResponseBody(response) {
  if (response.status === 204) return {};

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return {};
    }
  }

  const text = await response.text().catch(() => '');
  return text ? { message: text } : {};
}

function getResponseError(response, body) {
  return body?.error || body?.message || `HTTP ${response.status}`;
}

/**
 * Make authenticated API request
 */
async function request(endpoint, options = {}) {
  const token = getAuthToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Safety net: abort a request that hangs far longer than any real endpoint,
  // so a stuck fetch surfaces a retryable error instead of an infinite spinner.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 45000);
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      signal: options.signal || controller.signal,
    });

    // Handle unauthorized
    if (response.status === 401) {
      clearPersistedHubSession();
      window.location.href = '/login';
      return { success: false, error: 'Unauthorized' };
    }

    const data = await readResponseBody(response);

    if (!response.ok) {
      return { success: false, error: getResponseError(response, data) };
    }

    return { success: true, data };

  } catch (error) {
    if (error?.name === 'AbortError') return { success: false, error: 'Request timed out — try Refresh' };
    console.error('API request failed:', error);
    return { success: false, error: error.message };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Short-TTL GET cache (stale-while-navigate). Repeat visits to a page — and the
 * per-page 60s live refresh cycle — no longer blank-spinner while the server
 * round-trips; a recent response paints instantly. TTL is deliberately under the
 * 60s auto-refresh interval so periodic refreshes always fetch fresh, and ANY
 * mutation (POST/PUT/DELETE/PATCH) or manual Refresh clears the cache so nothing
 * stale survives a write. Keyed by full endpoint (query string included).
 */
const _getCache = new Map();
const GET_CACHE_TTL = 30_000;
export function invalidateApiCache() { _getCache.clear(); }

// GET request (served from the short-TTL cache when fresh; pass {force:true} to bypass).
export const get = (endpoint, opts = {}) => {
  if (!opts.force) {
    const hit = _getCache.get(endpoint);
    if (hit && (Date.now() - hit.ts) < GET_CACHE_TTL) return Promise.resolve(hit.data);
  }
  return request(endpoint).then((res) => {
    if (res && res.success) _getCache.set(endpoint, { data: res, ts: Date.now() });
    return res;
  });
};

/**
 * NIGHT OPS ⌘K universal search (spec 01 §3.3, spec 04 F2). Additive helper —
 * harmless to ship now; the `GET /hub/search` aggregator is owned by the
 * smart-features backend (Phase 2). Until it exists, the palette uses the
 * existing per-list filters (Conversations `?search=`) + a client-side hold
 * filter, so this is unused for now.
 */
export const hubSearch = (q, types) =>
  get('/hub/search?q=' + encodeURIComponent(q) + (types ? '&types=' + types : ''));

// Any mutation may change what GETs return → drop the cache so reads are fresh.
const afterMutation = (res) => { invalidateApiCache(); return res; };

// POST request
const post = (endpoint, body) => request(endpoint, {
  method: 'POST',
  body: JSON.stringify(body),
}).then(afterMutation);

// PUT request
const put = (endpoint, body) => request(endpoint, {
  method: 'PUT',
  body: JSON.stringify(body),
}).then(afterMutation);

// DELETE request
const del = (endpoint) => request(endpoint, {
  method: 'DELETE',
}).then(afterMutation);

// PATCH request
const patch = (endpoint, body) => request(endpoint, {
  method: 'PATCH',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
}).then(afterMutation);

// ============================================
// Sandbox / Testing Lab
// ============================================
export const getSandboxTickets = (status) => get(`/hub/sandbox/tickets${status ? `?status=${status}` : ''}`);
export const getSandboxTicket = (ticketId) => get(`/hub/sandbox/tickets/${ticketId}`);
export const getSandboxRegressionPacks = () => get('/hub/sandbox/regression-packs');
export const launchSandboxRegressionRun = (data) => post('/hub/sandbox/regression-runs', data);
export const createSandboxTicket = (data) => post('/hub/sandbox/tickets', data);
export const sandboxReply = (ticketId, data) => post(`/hub/sandbox/tickets/${ticketId}/reply`, data);
export const sandboxProcess = (ticketId) => post(`/hub/sandbox/tickets/${ticketId}/process`, {});
export const updateSandboxTicket = (ticketId, data) => put(`/hub/sandbox/tickets/${ticketId}`, data);
export const deleteSandboxTicket = (ticketId) => del(`/hub/sandbox/tickets/${ticketId}`);
export const deleteSandboxMessagesBulk = (ticketId, messageIds) => post(`/hub/sandbox/tickets/${ticketId}/messages/delete`, { messageIds });
export const deleteSandboxTag = (tagId) => del(`/hub/sandbox/tags/${tagId}`);
export const deleteSandboxTagsByTicket = (ticketId) => del(`/hub/sandbox/tickets/${ticketId}/tags`);
export const sandboxUpload = async (file) => {
  const token = getAuthToken();
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_BASE}/hub/sandbox/upload`, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd });
  const data = await readResponseBody(res);
  if (!res.ok) return { success: false, error: getResponseError(res, data) };
  return data;
};

// ============================================
// API Endpoints
// ============================================

// Auth
export const login = (email, password) => post('/auth/login', { email, password });
export const logout = () => post('/auth/logout', {});

// Stats
export const getStats = () => get('/stats');

// Cases
export const getCases = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return get(`/hub/cases${query ? `?${query}` : ''}`);
};
export const getCase = (id) => get(`/hub/cases/${id}`);
export const updateCase = (id, data) => put(`/hub/cases/${id}`, data);

// Alerts
export const getAlerts = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return get(`/hub/alerts${query ? `?${query}` : ''}`);
};
export const getAlert = (id) => get(`/hub/alerts/${id}`);
export const getAlertTestingLabPayload = (id) => get(`/hub/alerts/${id}/testing-lab-payload`);
export const resolveAlert = (id, comment) => put(`/hub/alerts/${id}/resolve`, { comment });

export const sendAlertResponse = (id) => post(`/hub/alerts/${id}/send-response`, {});

// Returns
export const deleteAlert = (id) => del(`/hub/alerts/${id}`);
export const bulkDeleteAlerts = (ids) => post('/hub/alerts/bulk-delete', { ids });
export const getReturns = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return get(`/hub/returns${query ? `?${query}` : ''}`);
};
export const getReturn = (id) => get(`/hub/returns/${id}`);
export const createReturn = (data) => post('/hub/returns', data);
export const updateReturn = (id, data) => put(`/hub/returns/${id}`, data);
export const deleteReturn = (id) => del(`/hub/returns/${id}`);
export const bulkDeleteReturns = (ids) => post('/hub/returns/bulk-delete', { ids });
export const addReturnComment = (id, data) => post(`/hub/returns/${id}/comments`, data);
export const submitReturnReview = (id, data) => post(`/hub/returns/${id}/review`, data);
export const sendReturnRefundEmail = (id, data) => post(`/hub/returns/${id}/send-refund-email`, data);
// Disputes
export const getDisputes = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return get(`/hub/disputes${query ? `?${query}` : ''}`);
};
export const getDisputeStats = () => get('/hub/disputes/stats');
export const getDisputeAnalytics = (dateRange = 'all') => get(`/hub/disputes/analytics?dateRange=${dateRange}`);
export const getDispute = (id) => get(`/hub/disputes/${id}`);
export const updateDispute = (id, data) => put(`/hub/disputes/${id}`, data);
export const syncDisputes = () => post('/hub/disputes/sync', {});
export const uploadDisputeCSV = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const token = getAuthToken();
  const resp = await fetch(`${API_BASE}/hub/disputes/upload`, {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: formData,
  });
  const data = await readResponseBody(resp);
  if (!resp.ok) return { success: false, error: getResponseError(resp, data) };
  return data;
};

// Refund Executions
export const getRefundExecutions = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return get(`/hub/refunds${query ? `?${query}` : ''}`);
};
export const getRefundStats = () => get('/hub/refunds/stats');
export const getRefundAnalytics = (startDate, endDate) => get(`/hub/refunds/analytics?start=${startDate}&end=${endDate}`);
export const getRefundDetail = (id) => get(`/hub/refunds/${id}`);

// Held Orders
export const getHeldOrders = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return get(`/hub/held-orders${query ? `?${query}` : ''}`);
};
export const getHeldOrderStats = () => get('/hub/held-orders/stats');
export const importHeldOrders = (rows) => post('/hub/held-orders/import', { rows });

// Order Changes
export const getOrderChanges = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return get(`/hub/order-changes${query ? `?${query}` : ''}`);
};
export const getOrderChange = (id) => get(`/hub/order-changes/${id}`);
export const updateOrderChange = (id, data) => put(`/hub/order-changes/${id}`, data);
export const completeOrderChange = (id, data) => post(`/hub/order-changes/${id}/complete`, data);
export const deleteOrderChange = (id) => del(`/hub/order-changes/${id}`);
export const bulkDeleteOrderChanges = (ids) => post('/hub/order-changes/bulk-delete', { ids });

// Operations
export const getOperations = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return get(`/hub/operations${query ? `?${query}` : ''}`);
};
export const getOperationsConfig = () => get('/hub/operations/config');
export const getOperation = (id) => get(`/hub/operations/${id}`);
export const addOperationMessage = (id, data) => post(`/hub/operations/${id}/messages`, data);
export const getOperationConversations = (id) => get(`/hub/operations/${id}/conversations`);
export const reportIssue = (data) => post('/hub/operations/report-issue', data);
export const getTrackingIssues = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return get(`/hub/operations/tracking-issues${query ? `?${query}` : ''}`);
};
export const scanTrackingIssues = (data = {}) => post('/hub/operations/tracking-issues/scan', data);
export const regenerateTrackingIssue = (id, data = {}) => post(`/hub/operations/${id}/tracking-issue/regenerate`, data);
export const sendTrackingIssue = (id) => post(`/hub/operations/${id}/tracking-issue/send`, {});
export const bulkSendTrackingIssues = (operationIds) => post('/hub/operations/tracking-issues/bulk-send', { operationIds });

// User management (admin)
export const getPermissions = () => get('/hub/permissions');
export const getTeams = () => get('/hub/teams');
export const createTeam = (data) => post('/hub/teams', data);
export const updateTeam = (id, data) => put(`/hub/teams/${id}`, data);
export const deleteTeam = (id) => del(`/hub/teams/${id}`);
export const getTeamMembers = (id) => get(`/hub/teams/${id}/members`);
export const getRoles = () => get('/hub/roles');
export const createRole = (data) => post('/hub/roles', data);
export const updateRole = (id, data) => put(`/hub/roles/${id}`, data);
export const deleteRole = (id) => del(`/hub/roles/${id}`);
export const getUsers = () => get('/hub/users');
export const getUserDirectory = () => get('/hub/users/directory');
export const getUser = (id) => get(`/hub/users/${id}`);
export const getUserByName = (name) => get(`/hub/users/by-name/${encodeURIComponent(name)}`);
export const getUserViewContext = (id) => get(`/hub/users/${id}/view-context`);
export const impersonateUser = (id) => post(`/hub/users/${id}/impersonate`);
export const createUser = (data) => post('/hub/users', data);
export const updateUser = (id, data) => put(`/hub/users/${id}`, data);
export const resetUserPassword = (id, newPassword) => put(`/hub/users/${id}/password`, { newPassword });
export const deleteUser = (id) => del(`/hub/users/${id}`);

// FAQs
export const getFaqs = () => get('/hub/faqs');

// SOPs
export const getSops = () => get('/hub/sops');

// Products
export const getProducts = () => get('/hub/products');

// Policies
export const getPolicies = () => get('/hub/policies');

// Tickets
export const getTickets = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return get(`/hub/tickets${query ? `?${query}` : ''}`);
};
export const getActions = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return get(`/hub/actions${query ? `?${query}` : ''}`);
};
export const getTicket = (ticketId) => get(`/hub/tickets/${encodeURIComponent(ticketId)}`);
export const submitTicketFeedback = (ticketId, body) =>
  post(`/hub/tickets/${encodeURIComponent(ticketId)}/feedback`, body);
export const getTicketTestingLabPayload = (ticketId) => get(`/hub/tickets/${encodeURIComponent(ticketId)}/testing-lab-payload`);
export const getTicketLiveConversation = (ticketId) => get(`/hub/tickets/${encodeURIComponent(ticketId)}/live-conversation`);
export const getTicketStats = (startDate, endDate) =>
  get(`/hub/tickets/stats?start=${startDate}&end=${endDate}`);
export const bulkDeleteTickets = (ticketIds) =>
  post('/hub/tickets/bulk-delete', { ticketIds });
// Queued Responses
export const getTicketQueuedResponse = (ticketId) => get(`/hub/tickets/${encodeURIComponent(ticketId)}/queued-response`);
export const editQueuedResponse = (id, data) => put(`/hub/queued-responses/${id}`, data);
export const cancelQueuedResponse = (id) => del(`/hub/queued-responses/${id}`);
export const sendQueuedResponseNow = (id) => post(`/hub/queued-responses/${id}/send-now`, {});
export const retryQueuedResponseAI = (id) => post(`/hub/queued-responses/${id}/retry`, {});
export const retryTicketAI = (ticketId) => post(`/hub/tickets/${encodeURIComponent(ticketId)}/retry-ai`, {});
export const pauseQueuedResponse = (id) => post(`/hub/queued-responses/${id}/pause`, {});
export const resumeQueuedResponse = (id) => post(`/hub/queued-responses/${id}/resume`, {});

// Intent Registry
export const getIntents = () => get('/hub/intents');

// Analytics
export const getAnalytics = (startDate, endDate) => 
  get(`/hub/analytics?start=${startDate}&end=${endDate}`);

// AI Usage & Costs (admin)
export const getUsageStats = (startDate, endDate) =>
  get(`/hub/usage?start=${startDate}&end=${endDate}`);

// AI Costs debug: duplicate tickets, hourly distribution, top by cost
export const getCostDebug = (startDate, endDate) =>
  get(`/hub/ai-costs/debug?start=${startDate}&end=${endDate}`);

// Activity (optional actor email or target_user_id for per-user audit; admin only when set)
export const getActivity = (limit = 50, actor = null, targetUserId = null) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (actor) params.set('actor', actor);
  if (targetUserId) params.set('target_user_id', String(targetUserId));
  return get(`/hub/activity?${params.toString()}`);
};

// Inbox (tasks I'm tagged in); admin can pass user_id for another user
export const getInbox = (userId = null) => {
  const params = new URLSearchParams();
  if (userId) params.set('user_id', String(userId));
  return get(`/hub/inbox${params.toString() ? `?${params.toString()}` : ''}`);
};
export const resolveMention = (mentionId) => put(`/hub/inbox/resolve/${mentionId}`, {});

// Knowledge Gaps - Analytics
export const getKnowledgeGaps = () => get('/hub/knowledge-gaps');

// Dashboard Summary (AI-generated weekly digest)
export const getDashboardSummary = () => get('/hub/dashboard-summary');
export const generateDashboardSummary = () => post('/hub/dashboard-summary/generate', {});

// API Status / Health Checks
export const getApiStatus = () => get('/hub/api-status');

// GraphQL Fallback Tokens
export const getGraphQLTokenStatus = () => get('/hub/system/graphql-tokens');
export const updateGraphQLTokens = (data) => put('/hub/system/graphql-tokens', data);

// AI Config
export const getAIConfig = () => get('/hub/ai-config');
export const updateAIConfig = (key, value) => put('/hub/ai-config', { key, value });

// Profile
export const getProfile = () => get('/hub/profile');
export const updateProfile = (data) => put('/hub/profile', data);
export const changePassword = (data) => put('/hub/profile/password', data);

// AI Feedback (public form - no auth)
/** Upload screenshot file; returns { success: true, screenshotUrl } or { success: false, error } */
export async function uploadAiFeedbackScreenshot(file) {
  if (!file) return { success: true, screenshotUrl: null };
  try {
    const formData = new FormData();
    formData.append('screenshot', file);
    const res = await fetch(`${API_BASE}/feedback/upload`, {
      method: 'POST',
      body: formData,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: json.error || 'Upload failed' };
    return { success: true, screenshotUrl: json.screenshotUrl || null };
  } catch (e) {
    return { success: false, error: e.message || 'Upload failed' };
  }
}

export async function submitAiFeedbackPublic(data) {
  try {
    const res = await fetch(`${API_BASE}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        richpanelUrl: data.richpanelUrl || '',
        description: data.description,
        createdByName: data.createdByName,
        screenshotUrl: data.screenshotUrl || '',
        conversationNo: data.conversationNo || '',
        customerMessage: data.customerMessage || '',
        aiResponse: data.aiResponse || '',
        correctResponse: data.correctResponse || '',
        errorType: data.errorType || '',
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: json.error || 'Failed to submit' };
    return { success: true, data: json };
  } catch (e) {
    return { success: false, error: e.message || 'Network error' };
  }
}

// AI Feedback (hub - list + detail + export + delete + resolve + analyze)
export const getAiFeedback = (status = 'all') => get(`/hub/ai-feedback${status && status !== 'all' ? `?status=${status}` : ''}`);
export const getAiFeedbackById = (id) => get(`/hub/ai-feedback/${id}`);
export const deleteAiFeedback = (id) => del(`/hub/ai-feedback/${id}`);
export const deleteAiFeedbackBulk = (ids) => post('/hub/ai-feedback/bulk-delete', { ids });
export const resolveAiFeedback = (id) => post(`/hub/ai-feedback/${id}/resolve`, {});
export const updateAiFeedbackWorkflow = (id, workflowStatus) => patch(`/hub/ai-feedback/${id}/workflow`, { workflow_status: workflowStatus });
export const reopenAiFeedback = (id) => post(`/hub/ai-feedback/${id}/reopen`, {});
export const bulkResolveAiFeedback = (ids) => post('/hub/ai-feedback/bulk-resolve', { ids });
export const analyzeAiFeedback = (id) => post(`/hub/ai-feedback/${id}/analyze`, {});
export const analyzeAllAiFeedback = (page = 0) => post('/hub/ai-feedback/analyze-all', { page });
export const retestFeedbackItem = (id) => post(`/hub/ai-feedback/${id}/retest`, {});
export const retestAllFeedback = () => post('/hub/ai-feedback/retest-all', {});
export const getFeedbackTestingLabPayload = (id) => get(`/hub/ai-feedback/${id}/testing-lab-payload`);
export const getAiFeedbackAutomationState = () => get('/hub/ai-feedback/automation/state');
export const requestAiFeedbackAutomationRun = ({ ids = [], mode = 'selected' } = {}) => post('/hub/ai-feedback/automation/request-run', { ids, mode });

// Retention Analytics
export const getRetentionAnalytics = (startDate, endDate) => {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  return get(`/hub/retention/analytics?${params}`);
};

export const getRetentionTickets = (page = 1, limit = 20, outcome = '', startDate = '', endDate = '') => {
  const params = new URLSearchParams({ page, limit });
  if (outcome) params.set('outcome', outcome);
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  return get(`/hub/retention/tickets?${params}`);
};

export const analyzeRetentionInsights = (startDate = '', endDate = '', outcome = '') => {
  const params = new URLSearchParams();
  if (outcome) params.set('outcome', outcome);
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return post(`/hub/retention/analyze${query ? `?${query}` : ''}`, {});
};

export async function downloadRetentionCsv(startDate = '', endDate = '', outcome = '') {
  const token = getAuthToken();
  if (!token) return { success: false, error: 'Not logged in' };
  try {
    const params = new URLSearchParams();
    if (outcome) params.set('outcome', outcome);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const query = params.toString();
    const res = await fetch(`${API_BASE}/hub/retention/export${query ? `?${query}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return { success: false, error: 'Export failed' };
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `retention-insights-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message || 'Export failed' };
  }
}

// Ask Emma (read-only NL query over ops data — spec 04 F1). POSTs the question
// to the additive, read-only endpoint. The Worker routes BOTH /hub/ask and
// /hub/ask-emma to the same handler; use /hub/ask per the page contract.
// Returns the api wrapper shape { success, data } — on non-2xx, data is absent
// and `error` carries the server envelope's message (e.g. "Ask Emma is
// disabled", "Rate limit exceeded"). 429s also carry retryAfterSeconds in the
// raw body, surfaced via a dedicated caller below.
export const askEmma = (question, conversationId) =>
  post('/hub/ask', conversationId ? { question, conversationId } : { question });

export const getExportIntents = () => get('/hub/exports/intents');

export const getEmmaConversationExportPreview = (params = {}) => {
  const query = new URLSearchParams();
  const focusValue = Array.isArray(params.focus) ? params.focus.filter(Boolean).join(',') : params.focus;
  if (focusValue) query.set('focus', focusValue);
  const intentsValue = Array.isArray(params.intents) ? params.intents.filter(Boolean).join(',') : params.intents;
  if (intentsValue) query.set('intents', intentsValue);
  if (params.startDate) query.set('startDate', params.startDate);
  if (params.endDate) query.set('endDate', params.endDate);
  if (params.dateField) query.set('dateField', params.dateField);
  if (params.contentMode) query.set('contentMode', params.contentMode);
  if (params.includePrivateNotes) query.set('includePrivateNotes', 'true');
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  const fieldsValue = Array.isArray(params.fields) ? params.fields.filter(Boolean).join(',') : params.fields;
  if (fieldsValue) query.set('fields', fieldsValue);
  return get(`/hub/exports/emma-conversations/preview${query.toString() ? `?${query.toString()}` : ''}`);
};

export function saveEmmaConversationExportFile(file) {
  const url = URL.createObjectURL(file.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.filename || 'emma-export.txt';
  a.click();
  URL.revokeObjectURL(url);
}

export async function fetchEmmaConversationExportFile({
  focus = '',
  intents = '',
  startDate = '',
  endDate = '',
  dateField = '',
  contentMode = '',
  includePrivateNotes = false,
  cacheMode = '',
  limit = '',
  format = 'csv',
  fields = '',
} = {}) {
  const token = getAuthToken();
  if (!token) return { success: false, error: 'Not logged in' };
  try {
    const query = new URLSearchParams();
    const focusValue = Array.isArray(focus) ? focus.filter(Boolean).join(',') : focus;
    if (focusValue) query.set('focus', focusValue);
    const intentsValue = Array.isArray(intents) ? intents.filter(Boolean).join(',') : intents;
    if (intentsValue) query.set('intents', intentsValue);
    if (startDate) query.set('startDate', startDate);
    if (endDate) query.set('endDate', endDate);
    if (dateField) query.set('dateField', dateField);
    if (contentMode) query.set('contentMode', contentMode);
    if (includePrivateNotes) query.set('includePrivateNotes', 'true');
    if (cacheMode) query.set('cacheMode', cacheMode);
    if (limit) query.set('limit', limit);
    if (format) query.set('format', format);
    const fieldsValue = Array.isArray(fields) ? fields.filter(Boolean).join(',') : fields;
    if (fieldsValue) query.set('fields', fieldsValue);
    const res = await fetch(`${API_BASE}/hub/exports/emma-conversations/download${query.toString() ? `?${query.toString()}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const body = await readResponseBody(res);
      return { success: false, error: getResponseError(res, body) };
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const filenameMatch = disposition.match(/filename=\"?([^"]+)\"?/i);
    return { success: true, blob, filename: filenameMatch?.[1] || `emma-export.${format}` };
  } catch (e) {
    return { success: false, error: e.message || 'Export failed' };
  }
}

export async function downloadEmmaConversationExport(options = {}) {
  const result = await fetchEmmaConversationExportFile(options);
  if (!result.success) return result;
  saveEmmaConversationExportFile(result);
  return { success: true };
}

// Emma Performance Dashboard
export const getEmmaPerformance = () => get('/hub/analytics/emma-performance');

// Pending Refunds Queue
export const getPendingRefunds = () => get('/hub/refunds/pending');
export const approvePendingRefund = (id) => post(`/hub/refunds/pending/${id}/approve`, {});
export const rejectPendingRefund = (id, reason) => post(`/hub/refunds/pending/${id}/reject`, { reason });
export const modifyPendingRefund = (id, amount) => post(`/hub/refunds/pending/${id}/modify`, { amount });

// Snooze Follow-ups
export const getSnoozeFollowups = (params = {}) => {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.offset != null) query.set('offset', String(params.offset));
  return get(`/hub/snooze-followups${query.toString() ? `?${query.toString()}` : ''}`);
};
export const cancelSnoozeFollowup = (ticketId) => post(`/hub/snooze-followups/${encodeURIComponent(ticketId)}/cancel`, {});
export const extendSnoozeFollowup = (ticketId, days) => post(`/hub/snooze-followups/${encodeURIComponent(ticketId)}/extend`, { days });
export const closeSnoozeFollowup = (ticketId) => post(`/hub/snooze-followups/${encodeURIComponent(ticketId)}/close`, {});

// Haris Audits
export const getHarisAudits = () => get('/hub/audits');
export const getHarisAuditLatest = () => get('/hub/audits/latest');
export const getHarisAuditDetail = (id) => get(`/hub/audits/${id}`);

/** Triggers browser download of CSV; returns { success } or { success: false, error } */
export async function downloadAiFeedbackCsv() {
  const token = getAuthToken();
  if (!token) return { success: false, error: 'Not logged in' };
  try {
    const res = await fetch(`${API_BASE}/hub/ai-feedback/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return { success: false, error: 'Export failed' };
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ai-feedback.csv';
    a.click();
    URL.revokeObjectURL(url);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message || 'Export failed' };
  }
}
